import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, Validators} from "@angular/forms";
import {PeriodTypeEnum} from "../../../models/asset/period-type.enum";
import {AvailablePeriodDates} from "../../../../shared/model/period";
import {DateUtil} from "../../../../shared/utils/date-util";
import {Subject, takeUntil} from "rxjs";

export enum ActionType {
    Add = "ADD",
    Edit = "EDIT",
    Cancel = "CANCEL",
    Delete = "DELETE"
}

type AssetHistoryDialogData = {
    action: ActionType;
    periodType: string;
    periodKeyDate: string;
    originPeriodKeyDate: string;
    originPeriodType: string;
    gpProjected: boolean;
    availablePeriodDates: AvailablePeriodDates;
    isFullFiscalYearOnly: boolean;
};

@Component({
    selector: "valumize-asset-history-period-dialog",
    templateUrl: "./asset-history-period-dialog.component.html",
    standalone: false
})
export class AssetHistoryPeriodDialogComponent implements OnInit, OnDestroy {
    private readonly formBuilder = inject(FormBuilder);
    public  readonly dialogRef  = inject(MatDialogRef<AssetHistoryPeriodDialogComponent>);
    public  readonly data       = inject<AssetHistoryDialogData>(MAT_DIALOG_DATA);

    destroy$ = new Subject();

    action: ActionType = this.data.action;
    deletePeriod = false;
    originPeriodType = this.data.periodType;
    originPeriodKeyDate = this.data.periodKeyDate;
    // Array of available dates for period selection
    quarterEndDates = this.data.availablePeriodDates.QUARTER;
    semesterEndDates = this.data.availablePeriodDates.SEMESTER;
    fullFiscalYearEndDates = this.data.availablePeriodDates.FULL_FISCAL_YEAR;
    isFullFiscalYearOnly = this.data.isFullFiscalYearOnly;

    periodForm = this.formBuilder.group({
        periodType: this.formBuilder.control<string>("", {nonNullable: true, validators: [Validators.required]}),
        periodKeyDate: this.formBuilder.control<string>(""),
        periodKeyDatePicker: this.formBuilder.control<Date | null>(null),
        gpProjected: this.formBuilder.control<boolean>(false, {nonNullable: true}),
    });

    protected readonly ACTION_TYPE = ActionType;
    protected readonly PERIOD_TYPE = PeriodTypeEnum;

    constructor( ) {
    }

    ngOnInit(): void {
        const isEdit = this.data?.action === ActionType.Edit;
        const initialType = isEdit ? this.data.periodType : PeriodTypeEnum.FullFiscalYear;

        this.periodForm.patchValue({
            periodType: initialType,
            periodKeyDate: isEdit ? this.data.periodKeyDate : null,
            periodKeyDatePicker: isEdit && this.isRollingPeriod(initialType) && this.data.periodKeyDate
                ? new Date(this.data.periodKeyDate)
                : null,
            gpProjected: isEdit ? (this.data.gpProjected ?? false) : false,
        });

        // Set validators synchronously so nothing flips after the first check
        if (this.isRollingPeriod(initialType)) {
            this.periodForm.controls.periodKeyDate.clearValidators();
            this.periodForm.controls.periodKeyDatePicker.setValidators([Validators.required]);
        } else {
            this.periodForm.controls.periodKeyDate.setValidators([Validators.required]);
            this.periodForm.controls.periodKeyDatePicker.clearValidators();
        }
        this.periodForm.updateValueAndValidity({emitEvent: false});

        this.periodForm.get("periodType")!.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            const key = this.periodForm.get("periodKeyDate")!;
            const pick = this.periodForm.get("periodKeyDatePicker")!;
            const type = this.periodForm.controls.periodType.value;
            const rolling = this.isRollingPeriod(type);

            if (rolling) {
                key.clearValidators();
                pick.setValidators([Validators.required]);
                key.reset(null, {emitEvent: false});
            } else {
                pick.clearValidators();
                key.setValidators([Validators.required]);
                pick.reset(null, {emitEvent: false});
            }

            key.updateValueAndValidity({emitEvent: false});
            pick.updateValueAndValidity({emitEvent: false});
        });
    }

    onCancel(): void {
        this.dialogRef.close(ActionType.Cancel);
    }

    isRollingPeriod(periodType?: string | null): boolean {
        if (!periodType) {
            periodType = this.periodForm.controls.periodType.value;
        }
        return periodType === PeriodTypeEnum.LastTwelveMonths || periodType === PeriodTypeEnum.YearToDate;
    }

    onSave(): void {
        if (this.periodForm.valid) {
            const periodType = this.periodForm.controls.periodType.value;
            const periodKeyDate = this.periodForm.controls.periodKeyDate.value;
            const periodKeyDatePicker = this.periodForm.controls.periodKeyDatePicker.value;

            this.dialogRef.close({
                action: this.action,
                periodType: periodType,
                periodKeyDate: this.isRollingPeriod() ? DateUtil.toIsoDate(periodKeyDatePicker) : periodKeyDate,
                gpProjected: this.periodForm.controls.gpProjected.value,
                originPeriodKeyDate: this.originPeriodKeyDate,
                originPeriodType: this.originPeriodType,
                originGpProjected: this.data.gpProjected ?? false
            });
        }
    }

    onDelete(): void {
        this.deletePeriod = true;
    }

    onDeleteConfirm(): void {
        this.dialogRef.close(
            {
                action: ActionType.Delete,
                originPeriodKeyDate: this.originPeriodKeyDate,
                originPeriodType: this.originPeriodType,
                gpProjected: this.data.gpProjected ?? false
            });
    }

    onDeleteCancel(): void {
        this.deletePeriod = false;
    }

    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
